import {
  computeGazeVector,
  computeHeadVector,
  projectGazeToScreen,
  type EventEmitter,
  type EmotionalState,
} from '@alphahub/eie-core';
import type { SessionStateStore } from './SessionStateStore';

type Getters = {
  isRunning: () => boolean;
  getConfig: () => any;
  getFacialDetector: () => any;
  getHandDetector: () => any;
  getPoseDetector: () => any;
  getLocalClassifier: () => any;
  getLatestLandmarks: () => any[] | undefined;
  getLatestHandLandmarks: () => any[] | undefined;
  getSessionId: () => string;
};

type Setters = {
  setLatestBlendshapes: (bs: Record<string, number>) => void;
  setLatestLandmarks: (lms: any[]) => void;
  startLocalCalibration: (anchor?: { x: number; y: number }) => void;
};

/**
 * LightDetectionLoop encapsulates the interval-based detection tick
 * previously inlined as startLightDetectionLoop / runLightDetectionTick in EIESession.
 */
export class LightDetectionLoop {
  private intervalId?: number;
  private wasEmotionCalibrating = false;
  private readonly HAND_BUFFER_SIZE = 5;
  private readonly HAND_BUFFER_REQUIRED = 3;
  private readonly COGNITIVE_BUFFER_SIZE = 3;
  /** Minimum blendshape keys for a usable frame (rejects degraded 3-key frames on mobile) */
  private readonly MIN_BLENDSHAPE_KEYS = 15;

  constructor(
    private emitter: EventEmitter,
    private state: SessionStateStore,
    private getters: Getters,
    private setters: Setters,
  ) {}

  private normalInterval = 200;
  private calibrationInterval = 100;

  start(): void {
    const configured = this.getters.getConfig().intervals.emotion;
    this.normalInterval = configured;
    this.calibrationInterval = configured > 120 ? Math.round(configured / 2) : configured;
    this.scheduleTick();
  }

  private scheduleTick(): void {
    const localClassifier = this.getters.getLocalClassifier();
    const isCalibrating = localClassifier?.getIsCalibrating?.() ?? false;
    const interval = isCalibrating ? this.calibrationInterval : this.normalInterval;
    this.intervalId = window.setTimeout(() => {
      this.tick().then(() => this.scheduleTick());
    }, interval) as unknown as number;
  }

  stop(): void {
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = undefined;
    }
  }

  async tick(): Promise<void> {
    if (!this.getters.isRunning() || !this.getters.getFacialDetector() || !this.getters.getLocalClassifier()) return;

    const detection = await this.getters.getFacialDetector().detect();
    if (!detection || !detection.blendshapes) {
      if (detection && (detection as any).faceLockPaused) {
        const pausedState: EmotionalState = {
          emotion: 'neutral',
          valence: 0,
          arousal: 0,
          confidence: 0,
          timestamp: Date.now(),
          cognitive: 'out_of_frame' as any,
        };
        this.emitter.emit('emotionUpdate', pausedState);
        this.emitter.emit('cognitiveUpdate', {
          state: 'out_of_frame',
          confidence: 1,
          rawState: 'out_of_frame',
          timestamp: Date.now(),
        } as any);
      }
      // During calibration, signal face loss so UI can inform the user
      const localClassifier = this.getters.getLocalClassifier();
      if (localClassifier?.getIsCalibrating?.()) {
        const progress = localClassifier.getCalibrationProgress?.();
        if (progress) {
          this.emitter.emit('emotionCalibrationProgress', { ...progress, faceDetected: false });
        }
      }
      return;
    }

    this.setters.setLatestBlendshapes(detection.blendshapes);

    // Reject degraded frames with too few blendshape keys (e.g. 3-key NaN fallback on mobile)
    const bsKeyCount = Object.keys(detection.blendshapes).filter(k => !k.startsWith('__')).length;
    if (bsKeyCount < this.MIN_BLENDSHAPE_KEYS) {
      return;
    }

    if (detection.faceLandmarks) {
      this.setters.setLatestLandmarks(detection.faceLandmarks);
      this.emitter.emit('faceLandmarks', detection.faceLandmarks);
    }

    if (
      this.state.pendingLocalCalibration &&
      detection.faceLandmarks &&
      detection.faceLandmarks.length > 0
    ) {
      const pending = this.state.pendingLocalCalibration;
      if (pending.calibrationReadyAt === undefined) {
        const delay = this.getters.getConfig().camera?.calibrationDelay ?? 0;
        pending.calibrationReadyAt = Date.now() + delay;
        if (delay > 0) console.info(`[EIE][CAL] face detected, waiting ${delay}ms for AE to settle before calibration`);
      }
      if (Date.now() < (pending.calibrationReadyAt ?? 0)) return;
      console.info('[EIE][CAL] face-ready-starting', {
        sessionId: this.getters.getSessionId(),
        screenAnchor: pending.screenAnchor ?? null,
        landmarkCount: detection.faceLandmarks.length,
        blendshapeCount: Object.keys(detection.blendshapes).length,
      });
      this.setters.startLocalCalibration(pending.screenAnchor);
      return;
    }

    if (this.getters.getPoseDetector()) {
      const pose = this.getters.getPoseDetector().detect();
      if (pose) {
        detection.bodyPose = { keypoints: pose.keypoints };
        this.state.poseSnapshots.push(pose);
        this.emitter.emit('bodyPoseLandmarks', pose);
      }
    }

    const handData = this.getters.getHandDetector()?.detect();

    let smoothedHandNearFace = false;
    let rawHandNearFace = false;
    try {
      rawHandNearFace = !!handData?.nearFace;
      this.state.handNearFaceBuffer.push(rawHandNearFace);
      if (this.state.handNearFaceBuffer.length > this.HAND_BUFFER_SIZE) this.state.handNearFaceBuffer.shift();
      const trueCount = this.state.handNearFaceBuffer.filter(Boolean).length;
      smoothedHandNearFace = trueCount >= this.HAND_BUFFER_REQUIRED;
    } catch {
      smoothedHandNearFace = !!handData?.nearFace;
    }

    this.emitter.emit('handGesture', {
      raw: rawHandNearFace,
      smoothed: smoothedHandNearFace,
      handLandmarks: this.getters.getLatestHandLandmarks(),
      handData,
    });

    const localClassifier = this.getters.getLocalClassifier();
    let state: EmotionalState | null | undefined;
    if (localClassifier) {
      try {
        let headRotationForClassifier: { yaw: number; pitch: number; roll: number } | undefined;
        try {
          const headVec = computeHeadVector(detection.faceLandmarks as any);
          const headOffset = localClassifier?.getHeadCalibrationOffset?.() ?? null;
          if (headVec) {
            headRotationForClassifier = {
              yaw: headVec.yaw - (headOffset?.yaw ?? 0),
              pitch: headVec.pitch - (headOffset?.pitch ?? 0),
              roll: 0,
            };
          }
        } catch {
          headRotationForClassifier = undefined;
        }

        const res = (localClassifier as any).classify(
          detection.blendshapes,
          detection.faceLandmarks,
          headRotationForClassifier,
          smoothedHandNearFace,
          this.getters.getLatestHandLandmarks(),
        );
        state = res instanceof Promise ? await res : res;
      } catch (err) {
        console.warn('Classifier error:', err);
        state = null;
      }
    }

    if (!state) {
      if (localClassifier.getIsCalibrating()) {
        this.wasEmotionCalibrating = true;
        const progress = localClassifier.getCalibrationProgress?.();
        if (progress) {
          this.emitter.emit('emotionCalibrationProgress', progress);
        }
        return;
      }
      return;
    }

    // Detect calibration→ready transition
    if (this.wasEmotionCalibrating) {
      this.wasEmotionCalibrating = false;
      this.emitter.emit('emotionCalibrationProgress', { samples: 0, required: 0, progress: 1 });
    }

    try {
      const isMobileLightMode = !!this.getters.getConfig().camera?.isMobileRuntime;
      if (isMobileLightMode) {
        const hasFace = !!detection.blendshapes && Object.keys(detection.blendshapes).length > 5;
        (state as any).cognitive = hasFace ? 'engaged' : 'out_of_frame';
        (state as any).cognitiveConfidence = hasFace ? 0.9 : 0;
      } else if (localClassifier?.getCognitiveState) {
        const cognitive = localClassifier.getCognitiveState(detection.faceLandmarks, detection.blendshapes, (detection as any).headRotation);
        if (cognitive) {
          (state as any).cognitive = cognitive.state;
          (state as any).cognitiveConfidence = cognitive.confidence ?? 0;
          try {
            const gazeVec = computeGazeVector(this.getters.getLatestLandmarks() as any);
            const gazeOffset = localClassifier?.getGazeCalibrationOffset?.() ?? null;
            const screenAnchor = localClassifier?.getScreenCalibrationAnchor?.() ?? null;
            const adjH = gazeVec ? gazeVec.h - (gazeOffset?.h ?? 0) : null;
            const adjV = gazeVec ? gazeVec.v - (gazeOffset?.v ?? 0) : null;
            const gazeNorm = gazeVec
              ? projectGazeToScreen({ h: adjH ?? 0, v: adjV ?? 0 }, screenAnchor ?? undefined)
              : null;
            const tolerantMargin = 0.16;
            const gazeInScreen = !!gazeNorm && gazeNorm.x >= -tolerantMargin && gazeNorm.x <= 1 + tolerantMargin && gazeNorm.y >= -tolerantMargin && gazeNorm.y <= 1 + tolerantMargin;
            const headVec = computeHeadVector(this.getters.getLatestLandmarks() as any);
            const headOffset = localClassifier?.getHeadCalibrationOffset?.() ?? null;
            const headDelta = headVec
              ? { yaw: headVec.yaw - (headOffset?.yaw ?? 0), pitch: headVec.pitch - (headOffset?.pitch ?? 0) }
              : null;
            console.debug('[EIE][COG]', {
              state: cognitive.state,
              confidence: Number((cognitive.confidence ?? 0).toFixed(2)),
              gazeRaw: gazeVec,
              gazeOffset,
              gazeAdjusted: gazeVec ? { h: adjH, v: adjV } : null,
              gazeNorm,
              gazeInScreen,
              screenAnchor,
              headRaw: headVec,
              headOffset,
              headDelta,
            });
            (state as any).gazeRaw = gazeVec ? { h: gazeVec.h, v: gazeVec.v } : null;
            (state as any).gazeAdjusted = gazeVec ? { h: adjH, v: adjV } : null;
            (state as any).gazeOffset = gazeOffset;
            (state as any).gazeNorm = gazeNorm;
            (state as any).gazeInScreen = gazeInScreen;
            (state as any).headRotation = headDelta;
          } catch (e) {
            console.warn('[EIE][COG] gaze debug failed', e);
          }
        }
      }
    } catch (err) {
      console.warn('Cognitive analysis failed:', err);
    }

    try {
      const cognitiveState = (state as any).cognitive ?? 'engaged';
      const cognitiveConfidence = (state as any).cognitiveConfidence ?? 0;
      try {
        this.state.cognitiveStateBuffer.push(cognitiveState);
        if (this.state.cognitiveStateBuffer.length > this.COGNITIVE_BUFFER_SIZE) this.state.cognitiveStateBuffer.shift();
      } catch { this.state.cognitiveStateBuffer = [cognitiveState]; }

      const counts: Record<string, number> = {};
      for (const s of this.state.cognitiveStateBuffer) counts[s] = (counts[s] || 0) + 1;
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const smoothed = entries[0]?.[0] ?? cognitiveState;

      this.emitter.emit('cognitiveUpdate', {
        state: smoothed,
        confidence: cognitiveConfidence,
        rawState: cognitiveState,
        timestamp: Date.now(),
        gazeRaw: (state as any).gazeRaw ?? null,
        gazeAdjusted: (state as any).gazeAdjusted ?? null,
        gazeOffset: (state as any).gazeOffset ?? null,
        gazeNorm: (state as any).gazeNorm ?? null,
        gazeInScreen: (state as any).gazeInScreen ?? null,
        headRotation: (state as any).headRotation ?? null,
      } as any);
      const isEngaged = smoothed === 'engaged' || smoothed === 'focus';

      try {
        const yawnScore = (state as any).yawnScore ?? (state as any).yawn ?? 0;
        const yawnDetected = (state as any).yawnDetected ?? (yawnScore >= (this.getters.getConfig().localClassifier?.yawnThreshold ?? 0.45));
        this.emitter.emit('yawn', { detected: !!yawnDetected, score: Number(yawnScore), timestamp: Date.now(), cognitive: smoothed });
      } catch {
        // ignore
      }

      if (!isEngaged && this.getters.getConfig().localClassifier?.suppressWhenUnengaged !== false) return;
    } catch (err) {
      console.warn('cognitiveUpdate emit failed', err);
    }

    this.state.timeline.push(state);

    const now = Date.now();
    this.state.emotionBuffer.push({ emotion: state.emotion, confidence: state.confidence ?? 0, timestamp: now });
    const persistenceMs = this.getters.getConfig().localClassifier?.persistenceMs ?? 1500;
    while (this.state.emotionBuffer.length > 0 && now - this.state.emotionBuffer[0].timestamp > Math.max(persistenceMs * 2, 3000)) {
      this.state.emotionBuffer.shift();
    }

    const windowStart = now - persistenceMs;
    const recent = this.state.emotionBuffer.filter(e => e.timestamp >= windowStart);
    if (recent.length > 0) {
      const counts: Record<string, number> = {};
      for (const r of recent) counts[r.emotion] = (counts[r.emotion] || 0) + 1;
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const [candidate, cnt] = entries[0];
      const required = Math.ceil(recent.length * 0.6);
      if (cnt >= required) {
        if (this.state.lastStableEmotion !== candidate) {
          this.state.lastStableEmotion = candidate;
          this.emitter.emit('stableEmotion', {
            emotion: candidate,
            timestamp: now,
            recentCount: cnt,
            windowSize: recent.length,
            state: state,
          });
        }
      }
    }

    if (!this.state.baseline && this.state.timeline.length >= 60) {
      const vals = this.state.timeline.slice(0, 60);
      this.state.baseline = {
        avgValence: vals.reduce((s, v) => s + v.valence, 0) / vals.length,
        avgArousal: vals.reduce((s, v) => s + v.arousal, 0) / vals.length,
        timestamp: Date.now(),
      };
      this.emitter.emit('baselineCalibrated', this.state.baseline);
    }

    this.emitter.emit('emotionUpdate', state);
  }
}
